import gql from 'graphql-tag';

const CHECKLIST_TEMPLATE_LIST_FRAGMENT = gql`
	fragment checklistTemplateList on ChecklistTemplate {
		id
		title
		purpose
		items
		isActive
		requiresManagerApproval
		version
		created
		modified
	}
`;

const CHECKLIST_TEMPLATE_DETAIL_FRAGMENT = gql`
	fragment checklistTemplateDetail on ChecklistTemplate {
		id
		title
		purpose
		items
		requiresManagerApproval
		isActive
		version
		linkedProcedureDocument
	}
`;

const CHECKLIST_SUBMISSION_FRAGMENT = gql`
	fragment checklistSubmission on ChecklistSubmission {
		id
		template {
			id
			title
			purpose
			version
			requiresManagerApproval
		}
		purpose
		date
		serviceRun
		vehicle
		status
		submittedAt
		flaggedItemIds
		approvedAt
		approvedBy {
			id
			firstname
			lastname
		}
		signOffManager {
			id
			firstname
			lastname
		}
		signOffNote
	}
`;

export const CHECKLIST_SUBMISSION_AWAITING_SIGN_OFF_FRAGMENT = gql`
	fragment checklistSubmissionAwaitingSignOff on ChecklistSubmission {
		id
		template {
			id
			title
		}
		vehicleDetails {
			id
			name
			registration
		}
		user {
			id
			firstname
			lastname
			username
		}
		submittedAt
		serviceRun
	}
`;

export const GET_CHECKLIST_SUBMISSIONS_AWAITING_MY_SIGN_OFF = gql`
	query getChecklistSubmissionsAwaitingMySignOffQuery {
		getChecklistSubmissionsAwaitingMySignOff {
			...checklistSubmissionAwaitingSignOff
		}
	}
	${CHECKLIST_SUBMISSION_AWAITING_SIGN_OFF_FRAGMENT}
`;

export const GET_CHECKLIST_TEMPLATES = gql`
	query getChecklistTemplatesQuery(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: ChecklistTemplatePageFilter
	) {
		getChecklistTemplates(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...checklistTemplateList
			}
			count
		}
	}
	${CHECKLIST_TEMPLATE_LIST_FRAGMENT}
`;

export const GET_CHECKLIST_TEMPLATE = gql`
	query getChecklistTemplateQuery($id: String) {
		getChecklistTemplate(id: $id) {
			...checklistTemplateDetail
			created
			modified
		}
	}
	${CHECKLIST_TEMPLATE_DETAIL_FRAGMENT}
`;

export const GET_CHECKLIST_TEMPLATES_COUNT = gql`
	query {
		countChecklistTemplates
	}
`;

export const GET_ACTIVE_CHECKLIST_TEMPLATE = gql`
	query getActiveChecklistTemplateQuery($purpose: Int) {
		getActiveChecklistTemplate(purpose: $purpose) {
			...checklistTemplateDetail
		}
	}
	${CHECKLIST_TEMPLATE_DETAIL_FRAGMENT}
`;

export const GET_MY_CHECKLIST_SUBMISSIONS = gql`
	query getMyChecklistSubmissionsQuery($date: String!) {
		getMyChecklistSubmissions(date: $date) {
			...checklistSubmission
		}
	}
	${CHECKLIST_SUBMISSION_FRAGMENT}
`;

// The day's submissions with their saved answers (a submitted checklist shows read-only).
export const GET_MY_CHECKLIST_SUBMISSION_ANSWERS = gql`
	query getMyChecklistSubmissionAnswersQuery($date: String!) {
		getMyChecklistSubmissions(date: $date) {
			id
			purpose
			answers
			status
			submittedAt
			approvedAt
			approvedBy {
				id
				firstname
				lastname
			}
			signOffManager {
				id
				firstname
				lastname
			}
			signOffNote
		}
	}
`;

// The sign-off page a manager opens from the sign-off push notification.
export const GET_CHECKLIST_SUBMISSION = gql`
	query getChecklistSubmissionQuery($id: String) {
		getChecklistSubmission(id: $id) {
			...checklistSubmission
			answers
			template {
				id
				items
			}
			user {
				id
				firstname
				lastname
			}
			vehicleDetails {
				id
				name
			}
		}
	}
	${CHECKLIST_SUBMISSION_FRAGMENT}
`;

export const CREATE_CHECKLIST_TEMPLATE = gql`
	mutation createChecklistTemplateMutation($input: CreateChecklistTemplateInput!) {
		createChecklistTemplate(input: $input) {
			...checklistTemplateDetail
		}
	}
	${CHECKLIST_TEMPLATE_DETAIL_FRAGMENT}
`;

export const MODIFY_CHECKLIST_TEMPLATE = gql`
	mutation modifyChecklistTemplateMutation($input: ModifyChecklistTemplateInput!) {
		modifyChecklistTemplate(input: $input) {
			checklisttemplate {
				...checklistTemplateDetail
			}
		}
	}
	${CHECKLIST_TEMPLATE_DETAIL_FRAGMENT}
`;

export const DESTROY_CHECKLIST_TEMPLATE = gql`
	mutation destroyChecklistTemplateMutation($id: ID!) {
		destroyChecklistTemplate(id: $id)
	}
`;

export const SUBMIT_CHECKLIST = gql`
	mutation submitChecklistMutation(
		$templateId: ID!,
		$date: String!,
		$serviceRun: ID,
		$vehicle: ID,
		$managerSignOffBy: ID,
		$answers: JSON
	) {
		submitChecklist(
			templateId: $templateId,
			date: $date,
			serviceRun: $serviceRun,
			vehicle: $vehicle,
			managerSignOffBy: $managerSignOffBy,
			answers: $answers
		) {
			...checklistSubmission
			answers
			templateVersion
		}
	}
	${CHECKLIST_SUBMISSION_FRAGMENT}
`;

export const APPROVE_CHECKLIST_SUBMISSION = gql`
	mutation approveChecklistSubmissionMutation($id: ID!, $note: String) {
		approveChecklistSubmission(id: $id, note: $note) {
			id
			status
			approvedAt
			approvedBy {
				id
				firstname
				lastname
			}
			signOffNote
		}
	}
`;
